import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  Barcode,
  Search,
  Check,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  Image as ImageIcon,
  ScanLine,
  CheckCheck,
  X,
  Plus,
  Volume2,
  VolumeX,
  Sparkles,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Gtin,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
} from '../ui/index.js';
import { fetchJson } from '../lib/fetch-json.js';
import { playBeep, toCapitalizedCase } from '../lib/format.js';
import { repairGtinCheckDigit } from '../lib/gtin.js';
import { CreateWooDraftModal } from '../components/CreateWooDraftModal.js';
import { cn } from '../ui/cn.js';

interface ScanLookupItem {
  readonly id: number;
  readonly wcProductId?: number;
  readonly sku: string | null;
  readonly name: string;
  readonly brand: string | null;
  readonly image: string | null;
  readonly barcodeRaw: string | null;
  readonly eanRaw: string | null;
  readonly gtinCanonical: string | null;
  readonly regularPriceTtc?: number | null;
  readonly salePriceTtc?: number | null;
  readonly purchasePriceHt?: number | null;
  readonly stockQty?: number | null;
  readonly inStock?: boolean | null;
  readonly isPublished?: boolean;
  readonly isVisible?: boolean;
  readonly status?: string;
  readonly finalEanConfirmed?: boolean;
  readonly reviewStatus: 'approved' | 'rejected' | 'pending' | 'operator';
  readonly isApproved: boolean;
  readonly isRejected: boolean;
  readonly isDirectMatch: boolean;
}

interface CrawledOfferItem {
  readonly id: number;
  readonly name: string;
  readonly brand: string | null;
  readonly image: string | null;
  readonly priceTtc: number | null;
  readonly url: string | null;
  readonly sourceKey: string;
  readonly sourceLabel: string;
}

interface ScanLookupResponse {
  readonly code: string;
  readonly rawCode: string;
  readonly isValid: boolean;
  readonly matchType: 'none' | 'single' | 'variant_family' | 'already_approved';
  readonly items: readonly ScanLookupItem[];
  readonly crawledOffers?: readonly CrawledOfferItem[];
}

interface ProductSearchResult {
  readonly id: number;
  readonly wcProductId: number;
  readonly sku: string;
  readonly name: string;
  readonly brand: string | null;
  readonly images: readonly string[];
  readonly barcodeRaw: string | null;
  readonly eanRaw: string | null;
  readonly gtinCanonical: string | null;
  readonly regularPriceTtc: number | string | null;
  readonly salePriceTtc: number | string | null;
  readonly isPublished: boolean;
  readonly isVisible: boolean;
}

interface ScanHistoryEntry {
  readonly id: string;
  readonly code: string;
  readonly scannedAt: Date;
  readonly status: 'matched' | 'linked' | 'draft_created' | 'unmatched';
  readonly product?: {
    readonly id?: number | undefined;
    readonly wcProductId?: number | undefined;
    readonly name: string;
    readonly sku: string | null;
    readonly image: string | null;
  } | null | undefined;
}

interface DraftFormData {
  ean: string;
  name: string;
  brand: string;
  contenance: string;
  category: string;
  regularPrice: string;
  imageUrl: string;
  shortDescription: string;
  description: string;
}

function formatTnd(millimes: number | null | undefined): string {
  if (millimes === null || millimes === undefined || !Number.isFinite(millimes)) return '—';
  return (millimes / 1000).toFixed(3) + ' TND';
}

export function BarcodeCheckerPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [scannedCode, setScannedCode] = useState('');
  const [currentLookupCode, setCurrentLookupCode] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Search state when barcode has no match
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Modal display states
  const [isRecognizedModalOpen, setIsRecognizedModalOpen] = useState(false);
  const [isUnknownModalOpen, setIsUnknownModalOpen] = useState(false);
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);

  // Active product or draft state
  const [activeItem, setActiveItem] = useState<ScanLookupItem | null>(null);
  const [draftForm, setDraftForm] = useState<DraftFormData | null>(null);

  // Session scan history
  const [scanHistory, setScanHistory] = useState<readonly ScanHistoryEntry[]>([]);

  // Auto-focus input on page load and keep it focused
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Debounce product name search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Global keydown capture for warehouse barcode scanners
  useEffect(() => {
    let keyBuffer = '';
    let lastKeyTime = 0;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture when typing in inputs inside draft modal or search box
      if (
        document.activeElement === searchInputRef.current ||
        (document.activeElement instanceof HTMLInputElement &&
          document.activeElement !== inputRef.current) ||
        document.activeElement instanceof HTMLTextAreaElement
      ) {
        if (e.key === 'Escape') {
          setIsRecognizedModalOpen(false);
          setIsUnknownModalOpen(false);
          setIsDraftModalOpen(false);
          inputRef.current?.focus();
        }
        return;
      }

      if (e.key === 'Escape') {
        setIsRecognizedModalOpen(false);
        setIsUnknownModalOpen(false);
        setIsDraftModalOpen(false);
        inputRef.current?.focus();
        return;
      }

      const now = Date.now();
      const isDigit = /^[0-9]$/.test(e.key);

      if (e.key === 'Enter') {
        if (keyBuffer.length >= 8) {
          e.preventDefault();
          triggerScan(keyBuffer);
          keyBuffer = '';
        }
        return;
      }

      // Fast hardware scanner capture (< 150ms between key presses)
      if (isDigit) {
        if (now - lastKeyTime > 150) {
          keyBuffer = e.key;
        } else {
          keyBuffer += e.key;
        }
        lastKeyTime = now;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isDraftModalOpen]);

  // Lookup query for the current scanned code — ONLY fetch when code is non-empty
  const lookupQuery = useQuery({
    queryKey: ['scan-lookup', currentLookupCode],
    queryFn: () =>
      fetchJson<ScanLookupResponse>(
        `/api/v1/review/scan-lookup?code=${encodeURIComponent(currentLookupCode!)}`,
      ),
    enabled: currentLookupCode !== null && currentLookupCode.trim() !== '',
    retry: false,
  });

  // Search products query when code is unmatched
  const searchProductsQuery = useQuery({
    queryKey: ['catalog-search', debouncedSearch],
    queryFn: () =>
      fetchJson<{ total: number; items: readonly ProductSearchResult[] }>(
        `/api/v1/products?q=${encodeURIComponent(debouncedSearch)}&pageSize=12&site=published`,
      ),
    enabled: debouncedSearch.length >= 2,
  });

  // Link barcode to a selected product mutation
  const matchMutation = useMutation({
    mutationFn: (vars: {
      productId: number;
      barcode: string;
      productName?: string;
      image?: string | null;
      sku?: string | null;
    }) =>
      fetchJson<{ ok: boolean; productId: number; productName: string; gtin: string }>(
        '/api/v1/review/scan-match',
        {
          method: 'POST',
          body: JSON.stringify({ productId: vars.productId, barcode: vars.barcode }),
        },
      ),
    onSuccess: (data, vars) => {
      if (soundEnabled) playBeep('success');
      setStatusMessage(`✓ Code physique ${data.gtin} affecté à « ${data.productName} » (envoyé dans « À corriger Woo ») !`);

      // Add to session history
      setScanHistory((prev) => [
        {
          id: Math.random().toString(36).slice(2),
          code: vars.barcode,
          scannedAt: new Date(),
          status: 'linked',
          product: {
            id: vars.productId,
            name: vars.productName ?? data.productName,
            sku: vars.sku ?? null,
            image: vars.image ?? null,
          },
        },
        ...prev,
      ]);

      // Invalidate queries
      void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
      void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
      void queryClient.invalidateQueries({ queryKey: ['decisions'] });
      void queryClient.invalidateQueries({ queryKey: ['scan-lookup'] });

      // Close modals and reset
      setIsUnknownModalOpen(false);
      setIsRecognizedModalOpen(false);
      setCurrentLookupCode(null);
      setSearchQuery('');
      setDebouncedSearch('');
      setScannedCode('');

      // Auto refocus scanner
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    },
    onError: (err) => {
      if (soundEnabled) playBeep('error');
      setStatusMessage(`Erreur d'affectation : ${String(err.message)}`);
    },
  });


  const triggerScan = (codeToScan: string) => {
    const raw = codeToScan.trim();
    if (raw === '') return;

    const digits = raw.replace(/\D/g, '');
    const repaired = repairGtinCheckDigit(digits) ?? digits;

    setScannedCode(repaired);
    setCurrentLookupCode(repaired);
    setSearchQuery('');
    setDebouncedSearch('');
  };

  const handleManualScanSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    triggerScan(scannedCode);
  };

  // React to lookup result to open appropriate modal
  useEffect(() => {
    if (!lookupQuery.data || currentLookupCode === null) return;

    if (lookupQuery.data.items.length > 0) {
      if (soundEnabled) playBeep('success');
      const item = lookupQuery.data.items[0] ?? null;
      setActiveItem(item);
      setIsRecognizedModalOpen(true);
      setIsUnknownModalOpen(false);

      // Record in scan history (without duplicate)
      setScanHistory((prev) => {
        if (prev[0]?.code === lookupQuery.data?.code) return prev;
        return [
          {
            id: Math.random().toString(36).slice(2),
            code: lookupQuery.data!.code,
            scannedAt: new Date(),
            status: 'matched',
            product: item
              ? {
                  id: item.id,
                  wcProductId: item.wcProductId,
                  name: item.name,
                  sku: item.sku,
                  image: item.image,
                }
              : null,
          },
          ...prev,
        ];
      });
    } else {
      if (soundEnabled) playBeep('warning');
      setIsRecognizedModalOpen(false);
      setIsUnknownModalOpen(true);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 80);
    }
  }, [lookupQuery.data]);

  // Open Draft Modal prefilled from unknown barcode or crawled partner offer
  const openCreateDraftModal = (offer?: CrawledOfferItem | null) => {
    const cleanEan = currentLookupCode ? currentLookupCode.replace(/\D/g, '') : '';
    const name = offer ? toCapitalizedCase(offer.name) : '';
    const brand = offer?.brand ? toCapitalizedCase(offer.brand) : '';
    const image = offer?.image || '';
    const price = offer?.priceTtc !== null && offer?.priceTtc !== undefined
      ? (offer.priceTtc / 1000).toFixed(3)
      : '';

    const shortDesc = name
      ? `${name}${brand ? ` — ${brand}` : ''}. Code EAN : ${cleanEan}.`
      : `Nouveau produit — Code-barres EAN : ${cleanEan}.`;

    const longDesc =
      `<p><strong>${name || 'Nouveau produit'}</strong>${brand ? ` de la marque ${brand}` : ''}.</p>\n` +
      `<p><strong>Caractéristiques du produit :</strong></p>\n<ul>\n` +
      (brand ? `  <li><strong>Marque :</strong> ${brand}</li>\n` : '') +
      (cleanEan ? `  <li><strong>Code-barres (EAN) :</strong> ${cleanEan}</li>\n` : '') +
      `</ul>`;

    setDraftForm({
      ean: cleanEan,
      name,
      brand,
      contenance: '',
      category: '',
      regularPrice: price,
      imageUrl: image,
      shortDescription: shortDesc,
      description: longDesc,
    });
    setIsDraftModalOpen(true);
  };

  const isLookingUp = currentLookupCode !== null && lookupQuery.isFetching;

  // Session KPIs
  const totalScans = scanHistory.length;
  const recognizedCount = scanHistory.filter((s) => s.status === 'matched').length;
  const linkedCount = scanHistory.filter((s) => s.status === 'linked').length;
  const draftsCount = scanHistory.filter((s) => s.status === 'draft_created').length;

  return (
    <div className="space-y-6">
      <PageHeader
        index="S24"
        title="Barcode Checker (Entrepôt)"
        purpose="Scanner mobile et douchette physique pour l'entrepôt : vérification instantanée du site en zéro-clic, correction immédiate des codes erronés (le code physique fait foi), et création de brouillons WooCommerce pour les nouveaux produits."
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSoundEnabled((prev) => !prev)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all cursor-pointer',
                soundEnabled
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                  : 'border-border bg-card text-ink-ghost',
              )}
              title={soundEnabled ? 'Son activé (bip au scan)' : 'Son coupé'}
            >
              {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              <span>{soundEnabled ? 'Bip Actif' : 'Muet'}</span>
            </button>
            <Link to="/workbench/corrections">
              <Button variant="outline" size="sm" icon={<ExternalLink className="h-3.5 w-3.5" />}>
                Ouvrir « À corriger (Woo) »
              </Button>
            </Link>
            <Link to="/workbench">
              <Button variant="ghost" size="sm">
                GTIN Workbench
              </Button>
            </Link>
          </div>
        }
      />

      {statusMessage && (
        <Alert
          tone="good"
          title="Opération réussie"
          actions={
            <button
              type="button"
              onClick={() => setStatusMessage(null)}
              className="text-xs font-bold opacity-75 hover:opacity-100 p-1 cursor-pointer"
            >
              ✕
            </button>
          }
        >
          {statusMessage}
        </Alert>
      )}

      {/* Main Scanner Section - Zero Click Priority */}
      <Panel className="border-2 border-herb-600/50 bg-linear-to-b from-herb-50/40 via-card to-card shadow-lg">
        <PanelBody className="p-6 sm:p-8">
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-herb-600 text-white shadow-herb-600/30 shadow-xl">
              <ScanLine className="h-8 w-8 animate-pulse" />
              <div className="absolute -inset-1 rounded-2xl border-2 border-herb-400/50 animate-ping pointer-events-none" />
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-ink">Poste de Scan Actif (Entrepôt)</h2>
            <p className="mt-1 text-xs sm:text-sm text-ink-soft max-w-lg">
              Scannez le produit physique avec votre douchette ou saisissez les chiffres ci-dessous. Le résultat s'ouvre automatiquement.
            </p>

            <form onSubmit={handleManualScanSubmit} className="mt-6 w-full max-w-lg flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  ref={inputRef}
                  type="text"
                  autoFocus
                  value={scannedCode}
                  onChange={(e) => setScannedCode(e.target.value)}
                  placeholder="Scanner ou saisir un code EAN..."
                  className="h-13 w-full rounded-2xl border-2 border-herb-600 bg-card pl-12 pr-4 font-mono text-xl font-bold text-ink placeholder:text-ink-ghost focus:border-herb-700 focus:outline-none focus:ring-4 focus:ring-herb-500/20 shadow-md transition-all"
                />
                <Barcode className="absolute left-4 top-4 h-5 w-5 text-herb-600" />
              </div>
              <Button
                type="submit"
                variant="primary"
                size="md"
                disabled={isLookingUp || scannedCode.trim() === ''}
                icon={<Check className="h-4 w-4" />}
                className="h-13 px-6 rounded-2xl text-sm font-bold shadow-md cursor-pointer"
              >
                {isLookingUp ? 'Recherche…' : 'Vérifier'}
              </Button>
            </form>

            {/* Quick Helper */}
            <div className="mt-3 flex items-center gap-2 text-[11px] text-ink-faint">
              <span className="font-mono bg-cream px-1.5 py-0.5 rounded border border-border">Entrée</span>
              <span>ou bip douchette lance la vérification instantanée</span>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Loading state: ONLY show when actively fetching a real code */}
      {isLookingUp && (
        <div className="rounded-2xl border border-herb-300 bg-herb-50/50 p-6 text-center shadow-xs">
          <Loading label={`Recherche du code-barres ${currentLookupCode} sur WooCommerce…`} />
        </div>
      )}

      {/* Session KPIs Bar */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-border bg-card p-3 shadow-2xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-ink-ghost block">
            Scans Session
          </span>
          <span className="mt-1 text-2xl font-black text-ink font-mono block">{totalScans}</span>
        </div>
        <div className="rounded-xl border border-emerald-300 bg-emerald-50/60 p-3 shadow-2xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-800 block">
            Reconnus sur Woo
          </span>
          <span className="mt-1 text-2xl font-black text-emerald-900 font-mono block">
            {recognizedCount}
          </span>
        </div>
        <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-3 shadow-2xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-amber-800 block">
            Affectés / Corrigés
          </span>
          <span className="mt-1 text-2xl font-black text-amber-900 font-mono block">
            {linkedCount}
          </span>
        </div>
        <div className="rounded-xl border border-sky-300 bg-sky-50/60 p-3 shadow-2xs">
          <span className="text-[11px] font-bold uppercase tracking-wider text-sky-800 block">
            Brouillons Créés
          </span>
          <span className="mt-1 text-2xl font-black text-sky-900 font-mono block">{draftsCount}</span>
        </div>
      </div>

      {/* Session History Feed */}
      {scanHistory.length > 0 && (
        <Panel>
          <PanelBody className="space-y-3">
            <div className="flex items-center justify-between border-b border-border/80 pb-2">
              <h3 className="text-sm font-bold text-ink">Historique des Scans en Entrepôt</h3>
              <span className="text-xs text-ink-soft">{scanHistory.length} scan(s)</span>
            </div>

            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {scanHistory.map((entry) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-2.5 text-xs hover:border-herb-500 transition-all shadow-2xs"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {entry.product?.image ? (
                      <img
                        src={entry.product.image}
                        alt=""
                        className="h-10 w-10 shrink-0 rounded-lg border border-border object-contain bg-white p-0.5"
                      />
                    ) : (
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-cream text-ink-ghost">
                        <ImageIcon className="h-5 w-5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-ink truncate leading-tight">
                        {entry.product?.name ? toCapitalizedCase(entry.product.name) : 'Code non associé'}
                      </div>
                      <div className="font-mono text-[11px] text-ink-soft mt-0.5">
                        <span className="font-bold text-ink">{entry.code}</span>
                        {entry.product?.sku && <span> · SKU: {entry.product.sku}</span>}
                        {entry.product?.wcProductId && (
                          <span> · Woo #{entry.product.wcProductId}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {entry.status === 'matched' && <Badge tone="good">✓ Reconnu</Badge>}
                    {entry.status === 'linked' && <Badge tone="signal">✓ Affecté Woo</Badge>}
                    {entry.status === 'draft_created' && (
                      <Badge tone="signal">✓ Brouillon Créé</Badge>
                    )}
                    {entry.status === 'unmatched' && <Badge tone="alarm">Inconnu</Badge>}
                    <span className="font-mono text-[10px] text-ink-faint">
                      {entry.scannedAt.toLocaleTimeString('fr-FR', {
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </PanelBody>
        </Panel>
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: PRODUIT RECONNU SUR LE SITE (ProductRecognizedModal)             */}
      {/* ========================================================================= */}
      {isRecognizedModalOpen && activeItem &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs overflow-y-auto animate-fade"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsRecognizedModalOpen(false);
                inputRef.current?.focus();
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-recognized-title"
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border-2 border-emerald-500/40 bg-card p-6 shadow-2xl space-y-5 my-auto animate-scale"
            >
              {/* Header */}
              <div className="flex items-start justify-between border-b border-border/80 pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-md">
                    <CheckCircle2 className="h-7 w-7" />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 block">
                      ✓ Produit Identifié sur le Site WooCommerce
                    </span>
                    <h3 id="modal-recognized-title" className="text-lg font-black text-ink leading-tight">
                      {toCapitalizedCase(activeItem.name)}
                    </h3>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setIsRecognizedModalOpen(false);
                    inputRef.current?.focus();
                  }}
                  className="rounded-xl p-1.5 text-ink-ghost hover:bg-cream hover:text-ink cursor-pointer transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Body: Product Info & Image */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 items-center">
                <div className="flex justify-center">
                  {activeItem.image ? (
                    <img
                      src={activeItem.image}
                      alt={activeItem.name}
                      className="h-44 w-44 rounded-2xl border border-border bg-white object-contain p-2 shadow-xs"
                    />
                  ) : (
                    <div className="flex h-44 w-44 items-center justify-center rounded-2xl border border-border bg-cream text-ink-ghost">
                      <ImageIcon className="h-12 w-12" />
                    </div>
                  )}
                </div>

                <div className="sm:col-span-2 space-y-3">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-xl border border-border bg-cream/40 p-2.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-ink-soft block">
                        SKU
                      </span>
                      <span className="font-mono font-bold text-ink mt-0.5 block">
                        {activeItem.sku ?? 'Sans référence'}
                      </span>
                    </div>
                    <div className="rounded-xl border border-border bg-cream/40 p-2.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-ink-soft block">
                        Marque
                      </span>
                      <span className="font-bold text-ink mt-0.5 block">
                        {activeItem.brand ?? '—'}
                      </span>
                    </div>
                    <div className="rounded-xl border border-border bg-cream/40 p-2.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-ink-soft block">
                        Prix Public TTC
                      </span>
                      <span className="font-bold text-ink mt-0.5 block">
                        {formatTnd(activeItem.regularPriceTtc)}
                      </span>
                    </div>
                    <div className="rounded-xl border border-border bg-cream/40 p-2.5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-ink-soft block">
                        Statut WooCommerce
                      </span>
                      <span className="font-semibold text-emerald-800 mt-0.5 block">
                        {activeItem.isPublished ? 'En ligne (Publié)' : 'Brouillon / Caché'}
                      </span>
                    </div>
                  </div>

                  {/* Barcode Comparison Box */}
                  <div className="rounded-2xl border border-border bg-card p-3 space-y-2">
                    <div className="flex items-center justify-between text-xs">
                      <div>
                        <span className="text-ink-soft block text-[11px]">Code scanné en rayon :</span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <Gtin gtin={lookupQuery.data?.code} tone="scan" />
                          <span className="text-[10px] text-emerald-700 font-bold">(Vérité terrain)</span>
                        </div>
                      </div>

                      <div className="text-right">
                        <span className="text-ink-soft block text-[11px]">Code actuel sur Woo :</span>
                        <div className="mt-0.5">
                          {activeItem.barcodeRaw ? (
                            <span className="font-mono text-xs font-bold text-ink">
                              {activeItem.barcodeRaw}
                            </span>
                          ) : (
                            <span className="text-xs italic text-amber-600">Aucun code</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Discrepancy Alert & One-Click Correction */}
                    {activeItem.barcodeRaw &&
                    activeItem.barcodeRaw.replace(/\D/g, '') !==
                      lookupQuery.data?.code.replace(/\D/g, '') ? (
                      <div className="rounded-xl border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-950 space-y-2">
                        <div className="flex items-center gap-1.5">
                          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                          <span className="font-bold">
                            Le code physique scanné diffère du code enregistré sur WooCommerce.
                          </span>
                        </div>
                        <p className="text-[11px] text-amber-800 leading-tight">
                          Le produit physique dans vos mains est la vérité. Cliquez ci-dessous pour écraser le code erroné de WooCommerce et l'envoyer dans « À corriger ».
                        </p>
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          disabled={matchMutation.isPending}
                          onClick={() =>
                            matchMutation.mutate({
                              productId: activeItem.id,
                              barcode: lookupQuery.data!.code,
                              productName: activeItem.name,
                              image: activeItem.image,
                              sku: activeItem.sku,
                            })
                          }
                          className="w-full justify-center font-bold shadow-xs cursor-pointer"
                        >
                          {matchMutation.isPending
                            ? 'Mise à jour…'
                            : '⚡ Mettre à jour sur Woo (Envoyer vers À corriger)'}
                        </Button>
                      </div>
                    ) : (
                      <div className="rounded-xl bg-emerald-50/70 border border-emerald-200 p-2 flex items-center gap-2 text-xs text-emerald-900 font-semibold">
                        <CheckCheck className="h-4 w-4 text-emerald-700 shrink-0" />
                        <span>Code-barres 100% conforme entre le produit physique et WooCommerce.</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="pt-3 border-t border-border flex flex-wrap items-center justify-between gap-3">
                <span className="text-xs text-emerald-800 font-bold flex items-center gap-1.5">
                  <Check className="h-4 w-4" />
                  Prêt pour le scan suivant (scannez directement sans fermer)
                </span>

                <div className="flex items-center gap-2">
                  {activeItem.wcProductId && (
                    <a
                      href={`https://maparatunisie.tn/?p=${activeItem.wcProductId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-ink-soft hover:text-ink font-semibold px-2 py-1 rounded-lg border border-border hover:bg-cream transition-colors"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Voir sur le site
                    </a>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsRecognizedModalOpen(false);
                      inputRef.current?.focus();
                    }}
                  >
                    Fermer (Échap)
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ========================================================================= */}
      {/* MODAL 2: CODE INCONNU / NON RATTACHÉ (ProductUnknownModal)               */}
      {/* ========================================================================= */}
      {isUnknownModalOpen && currentLookupCode &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs overflow-y-auto animate-fade"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsUnknownModalOpen(false);
                inputRef.current?.focus();
              }
            }}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-unknown-title"
              className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border-2 border-amber-500/40 bg-card p-6 shadow-2xl space-y-5 my-auto animate-scale"
            >
              {/* Header */}
              <div className="flex items-start justify-between border-b border-border/80 pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-500 text-white shadow-md">
                    <AlertTriangle className="h-6 w-6" />
                  </div>
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-amber-700 block">
                      Code-barres Inconnu sur le Catalogue
                    </span>
                    <h3 id="modal-unknown-title" className="text-lg font-black text-ink font-mono">
                      {currentLookupCode}
                    </h3>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setIsUnknownModalOpen(false);
                    inputRef.current?.focus();
                  }}
                  className="rounded-xl p-1.5 text-ink-ghost hover:bg-cream hover:text-ink cursor-pointer transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Partner Crawler Offer (Pharma-shop, Parashop, B2B...) if discovered */}
              {lookupQuery.data?.crawledOffers && lookupQuery.data.crawledOffers.length > 0 && (
                <div className="rounded-2xl border-2 border-sky-400 bg-sky-50/70 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-sky-900 flex items-center gap-1.5">
                      <Sparkles className="h-4 w-4 text-sky-700" />
                      Produit Identifié chez nos partenaires crawlers ({lookupQuery.data.crawledOffers[0]?.sourceLabel}) :
                    </span>
                    <span className="text-[10px] font-mono text-sky-700 bg-white/80 px-2 py-0.5 rounded-md border border-sky-200">
                      EAN 100% Vérifié
                    </span>
                  </div>

                  {lookupQuery.data.crawledOffers.slice(0, 1).map((offer) => (
                    <div key={offer.id} className="flex items-center gap-3 bg-white p-3 rounded-xl border border-sky-200 shadow-2xs">
                      {offer.image ? (
                        <img
                          src={offer.image}
                          alt=""
                          className="h-14 w-14 shrink-0 rounded-lg object-contain border border-border p-1 bg-white"
                        />
                      ) : (
                        <div className="h-14 w-14 shrink-0 rounded-lg bg-cream flex items-center justify-center text-ink-ghost">
                          <ImageIcon className="h-6 w-6" />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-bold text-ink truncate leading-tight">
                          {toCapitalizedCase(offer.name)}
                        </h4>
                        <div className="text-xs text-ink-soft mt-0.5 flex items-center gap-2">
                          <span>{offer.brand ?? '—'}</span>
                          {offer.priceTtc && (
                            <span className="font-bold text-ink">· {formatTnd(offer.priceTtc)}</span>
                          )}
                        </div>
                      </div>

                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => openCreateDraftModal(offer)}
                        icon={<Plus className="h-4 w-4" />}
                        className="shrink-0 font-bold cursor-pointer"
                      >
                        Créer brouillon pré-rempli
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Section 1: Live Catalog Search */}
              <div className="space-y-3">
                <div>
                  <label
                    htmlFor="search-modal-product-input"
                    className="block text-xs font-bold text-ink-soft mb-1.5"
                  >
                    1. Le produit existe déjà sur WooCommerce avec un autre nom ou sans code ?
                  </label>
                  <div className="relative">
                    <input
                      id="search-modal-product-input"
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher par nom de produit (ex: Dexeryl, CeraVe, Uriage...)..."
                      className="h-11 w-full rounded-xl border-2 border-amber-500/80 bg-card pl-10 pr-4 text-sm font-semibold text-ink placeholder:text-ink-ghost focus:border-amber-600 focus:outline-none focus:ring-4 focus:ring-amber-500/20 shadow-xs"
                    />
                    <Search className="absolute left-3.5 top-3 h-5 w-5 text-amber-600" />
                  </div>
                </div>

                {/* Search Results */}
                {searchProductsQuery.isPending && (
                  <Loading label="Recherche des correspondances catalogue…" />
                )}

                {searchProductsQuery.data !== undefined &&
                  searchProductsQuery.data.items.length === 0 &&
                  debouncedSearch.length >= 2 && (
                    <div className="rounded-xl border border-border bg-cream/50 p-4 text-center text-xs text-ink-soft">
                      Aucun produit trouvé pour « {debouncedSearch} ». Essayez avec un mot-clé plus court.
                    </div>
                  )}

                {searchProductsQuery.data !== undefined && searchProductsQuery.data.items.length > 0 && (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {searchProductsQuery.data.items.map((prod) => (
                      <div
                        key={prod.id}
                        className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 hover:border-herb-500 transition-all shadow-xs"
                      >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {prod.images[0] ? (
                            <img
                              src={prod.images[0]}
                              alt={prod.name}
                              className="h-12 w-12 shrink-0 rounded-lg border border-border bg-white object-contain p-1"
                            />
                          ) : (
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-cream text-ink-ghost">
                              <ImageIcon className="h-6 w-6" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <h4 className="text-sm font-bold text-ink truncate leading-tight">
                              {toCapitalizedCase(prod.name)}
                            </h4>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 font-mono text-xs text-ink-soft">
                              <Badge tone="muted">SKU: {prod.sku || '∅'}</Badge>
                              <span>{prod.brand ?? '—'}</span>
                              {prod.barcodeRaw && (
                                <span className="text-[11px] text-ink-faint">
                                  Woo actuel: {prod.barcodeRaw}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          disabled={matchMutation.isPending}
                          onClick={() =>
                            matchMutation.mutate({
                              productId: prod.id,
                              barcode: currentLookupCode,
                              productName: prod.name,
                              image: prod.images[0] ?? null,
                              sku: prod.sku,
                            })
                          }
                          icon={<Check className="h-4 w-4" />}
                          className="shrink-0 font-bold cursor-pointer"
                        >
                          Associer & Corriger Woo
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Section 2: Create Brand New Product Button */}
              <div className="rounded-2xl border-2 border-dashed border-herb-400 bg-herb-50/40 p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div>
                  <span className="text-xs font-bold text-herb-900 block">
                    2. Produit totalement nouveau (Non référencé sur le site) ?
                  </span>
                  <p className="text-[11px] text-ink-soft mt-0.5">
                    Créez immédiatement la fiche en mode brouillon dans WooCommerce avec son code EAN officiel.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => openCreateDraftModal(null)}
                  icon={<Plus className="h-4 w-4" />}
                  className="shrink-0 font-bold shadow-xs cursor-pointer"
                >
                  ➕ Créer un nouveau produit
                </Button>
              </div>

              {/* Footer */}
              <div className="pt-3 border-t border-border flex items-center justify-between">
                <span className="text-xs text-ink-faint">
                  Pointez la douchette pour scanner un autre code à tout moment.
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsUnknownModalOpen(false);
                    inputRef.current?.focus();
                  }}
                >
                  Fermer (Échap)
                </Button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ========================================================================= */}
      {/* ========================================================================= */}
      {/* MODAL 3: CRÉATION BROUILLON WOOCOMMERCE (CreateWooDraftModal)             */}
      {/* ========================================================================= */}
      <CreateWooDraftModal
        isOpen={isDraftModalOpen && draftForm !== null}
        onClose={() => {
          setIsDraftModalOpen(false);
          setDraftForm(null);
          inputRef.current?.focus();
        }}
        subtitle="Nouveau Produit Entrepôt"
        initialData={draftForm}
        onSuccess={(data) => {
          if (soundEnabled) playBeep('success');
          setStatusMessage(
            `✓ Produit brouillon créé avec succès sur WooCommerce : « ${data.name} » (ID #${data.wcProductId}) avec l'EAN ${data.ean || draftForm?.ean} !`,
          );

          // Add to session history
          setScanHistory((prev) => [
            {
              id: Math.random().toString(36).slice(2),
              code: data.ean || draftForm?.ean || '',
              scannedAt: new Date(),
              status: 'draft_created',
              product: {
                wcProductId: data.wcProductId,
                name: data.name,
                sku: draftForm?.brand || null,
                image: draftForm?.imageUrl || null,
              },
            },
            ...prev,
          ]);

          // Close modals & reset
          setIsDraftModalOpen(false);
          setIsUnknownModalOpen(false);
          setDraftForm(null);
          setCurrentLookupCode(null);
          setScannedCode('');

          // Invalidate queries
          void queryClient.invalidateQueries({ queryKey: ['workbench-tab-counts'] });
          void queryClient.invalidateQueries({ queryKey: ['corrections-count'] });
          void queryClient.invalidateQueries({ queryKey: ['scan-lookup'] });

          // Auto refocus scanner
          setTimeout(() => {
            inputRef.current?.focus();
          }, 100);
        }}
      />
    </div>
  );
}
